/**
 * OpsMate 状态管理 Provider
 * 从 AZ_AI_MVR_H5/src/pages/OpsMate/model.tsx 迁移
 * 此文件只导出组件（OpsMateProvider），以兼容 Vite Fast Refresh
 * context / hook / 类型定义见 useOpsMateContext.ts
 *
 * 核心逻辑已拆分至:
 * - hooks/opsmate/useFeedback.ts       反馈（点赞/点踩/翻译）
 * - hooks/opsmate/useRegenerate.ts     消息重新生成
 * - hooks/opsmate/useSessionHistory.ts 历史会话加载
 * - hooks/opsmate/useMessageRoles.tsx  消息气泡渲染
 */
import { type ConversationsProps } from "@ant-design/x";
import { App, type GetProp } from "antd";
import { DashboardOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd/lib";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  OpsMateContext,
  type OpsMateContextType,
  type QuestionLibraryScope,
} from "./useOpsMateContext";
import SearchAskHuiIcon from "@/assets/opsmate/Search Ask_Hui.svg";
import MyMVRHuiIcon from "@/assets/opsmate/My MVR_Hui.svg";
import MyStudiesHuiIcon from "@/assets/opsmate/My Studies_Hui.svg";
import NewAskHuiIcon from "@/assets/opsmate/NewAsk_Hui.svg";
import NewAskNoIcon from "@/assets/opsmate/New Ask_No.svg";
import { searchConversation } from "@/services/opsmate/conversation";
import { listMyStudySources } from "@/services/studySource";
import { useAuth } from "@/context/auth";
import { SYSTEM_LOG_ROLES, userHasAnyRole } from "@/utils/user";

import { useFeedback } from "@/hooks/opsmate/useFeedback";
import { useRegenerate } from "@/hooks/opsmate/useRegenerate";
import { useSessionHistory } from "@/hooks/opsmate/useSessionHistory";
import { useMessageRoles } from "@/hooks/opsmate/useMessageRoles";
import {
  clearConversationSourceSelection,
  getConversationSourceSelection,
  migrateConversationSourceSelection,
  type ConversationSourceSelection,
  updateConversationOnlyUseSelectedSources,
  updateConversationSelectedSourceIndexes,
} from "./OpsMateContext.sourceSelection";

type MenuItem = Required<MenuProps>["items"][number];
const CONVERSATION_PAGE_SIZE = 30;
const NAV_GROUPS = {
  askOpsMate: "Ask OpsMate",
  adminConsole: "Admin Console",
} as const;

// ─── Provider 组件 ─────────────────────────────────────────────────────────

export function OpsMateProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const canAccessAdminArea = userHasAnyRole(user?.roles, SYSTEM_LOG_ROLES);
  const activeUserId = user?.id ?? "";
  const [studySourceAccess, setStudySourceAccess] = useState({ userId: "", allowed: false });
  const canAccessStudySource =
    activeUserId !== "" && studySourceAccess.userId === activeUserId && studySourceAccess.allowed;

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setStudySourceAccess({ userId: "", allowed: false });
      return () => undefined;
    }
    const requestedUserId = user.id;

    void listMyStudySources()
      .then((result) => {
        if (!cancelled) {
          setStudySourceAccess({ userId: requestedUserId, allowed: result.data.length > 0 });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStudySourceAccess({ userId: requestedUserId, allowed: false });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  // ── 基础状态 ──
  const [collapsed, setCollapsed] = useState(false);
  const [docsCollapsed, setDocsCollapsed] = useState(true);
  const [hasSelectedSources, setHasSelectedSources] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string>("add");
  const [pendingConversationId, setPendingConversationId] = useState<string | null>(null);
  const [conversationSourceSelections, setConversationSourceSelections] = useState<
    Map<string, ConversationSourceSelection>
  >(new Map());
  const currentConversationSourceSelection = useMemo(
    () => getConversationSourceSelection(conversationSourceSelections, selectedKeys),
    [conversationSourceSelections, selectedKeys],
  );
  const onlyUseSelectedSources = currentConversationSourceSelection.onlyUseSelectedSources;
  const selectedSourceIndexes = currentConversationSourceSelection.selectedSourceIndexes;
  const setOnlyUseSelectedSources: React.Dispatch<React.SetStateAction<boolean>> = useCallback(
    (updater) => {
      setConversationSourceSelections((current) =>
        updateConversationOnlyUseSelectedSources(current, selectedKeys, updater),
      );
    },
    [selectedKeys],
  );
  const setSelectedSourceIndexes: React.Dispatch<React.SetStateAction<Set<number>>> = useCallback(
    (updater) => {
      setConversationSourceSelections((current) =>
        updateConversationSelectedSourceIndexes(current, selectedKeys, updater),
      );
    },
    [selectedKeys],
  );
  const [openSearch, setOpenSearch] = useState(false);
  const [addSourcesVisible, setAddSourcesVisible] = useState(false);
  const [questionLibraryScope, setQuestionLibraryScope] = useState<QuestionLibraryScope>({
    docCategoryIds: [],
    studySourceIds: [],
    includePersonalDocs: false,
  });
  const [reasoningCollapsedByKey, setReasoningCollapsedByKey] = useState<Record<string, boolean>>(
    {},
  );
  const [highlightedDocGuid, setHighlightedDocGuid] = useState("");

  const [aiReasoning, setAIReasoning] = useState("");
  const [aiReasoningVisible, setAIReasoningVisible] = useState(false);

  const [itemsMap, setItemsMap] = useState<Map<string, any[]>>(new Map());
  const itemsMapRef = useRef<Map<string, any[]>>(new Map());
  itemsMapRef.current = itemsMap;
  const items = useMemo(() => itemsMap.get(selectedKeys) ?? [], [itemsMap, selectedKeys]);
  const itemsRef = useRef<any[]>([]);
  itemsRef.current = items;

  // 更新当前会话 items（供 useFeedback / useRegenerate 等只关心当前会话的 hook 使用）
  const setItems: React.Dispatch<React.SetStateAction<any[]>> = useCallback(
    (updater) => {
      setItemsMap((prev) => {
        const newMap = new Map(prev);
        const current = prev.get(selectedKeys) ?? [];
        newMap.set(selectedKeys, typeof updater === "function" ? updater(current) : updater);
        return newMap;
      });
    },
    [selectedKeys],
  );

  // 按 convId 更新指定会话的 items（供流式回调使用，不受 selectedKeys 切换影响）
  const setItemsForConversation = useCallback(
    (convId: string, updater: React.SetStateAction<any[]>) => {
      setItemsMap((prev) => {
        const newMap = new Map(prev);
        const current = prev.get(convId) ?? [];
        newMap.set(convId, typeof updater === "function" ? updater(current) : updater);
        return newMap;
      });
    },
    [],
  );

  // 读取指定会话的 items（通过 ref 实现稳定函数引用，可安全用于 effect 依赖）
  const getItemsForConversation = useCallback(
    (convId: string) => itemsMapRef.current.get(convId) ?? [],
    [],
  );
  // 手动添加的 Sources 按会话独立维护：从一个会话切到另一个会话时，上个会话手动
  // 添加的来源不应残留到新会话的右侧清单（与 itemsMap 同样按 selectedKeys 隔离）。
  const [manualSourcesMap, setManualSourcesMap] = useState<Map<string, any[]>>(new Map());
  const manualSources = useMemo(
    () => manualSourcesMap.get(selectedKeys) ?? [],
    [manualSourcesMap, selectedKeys],
  );
  const setManualSources: React.Dispatch<React.SetStateAction<any[]>> = useCallback(
    (updater) => {
      setManualSourcesMap((prev) => {
        const current = prev.get(selectedKeys) ?? [];
        const nextVal =
          typeof updater === "function" ? (updater as (p: any[]) => any[])(current) : updater;
        const newMap = new Map(prev);
        newMap.set(selectedKeys, nextVal);
        return newMap;
      });
    },
    [selectedKeys],
  );

  // 在 selectedKeys 切换前迁移首问状态，使目标会话第一次渲染就拿到完整选择，避免 effect
  // 执行顺序造成 only 模式或 checkbox 短暂丢失。普通左侧会话切换不会调用此方法。
  const migrateNewConversationSources = useCallback((conversationId: string) => {
    setManualSourcesMap((current) => {
      if (!current.has("add")) return current;
      const next = new Map(current);
      next.set(conversationId, current.get("add") ?? []);
      next.delete("add");
      return next;
    });
    setConversationSourceSelections((current) =>
      migrateConversationSourceSelection(current, "add", conversationId),
    );
  }, []);

  const [selectedSourceGuidNames, setSelectedSourceGuidNames] = useState<string[]>([]);

  const [historyOpsConversations, setHistoryOpsConversations] = useState<
    GetProp<ConversationsProps, "items">
  >([]);
  const [historyConversationsHasMore, setHistoryConversationsHasMore] = useState(true);
  const [historyConversationsLoadingMore, setHistoryConversationsLoadingMore] = useState(false);
  const historyConversationsPageRef = useRef(1);
  const historyConversationsHasMoreRef = useRef(true);
  const historyConversationsLoadingRef = useRef(false);

  const listRef = React.useRef<any>(null);

  // ── 高度计算 ──
  const pageHeight = "calc(100vh - 76px)";

  // ── 重置会话 ──
  const resetConversation = useCallback(() => {
    setSelectedKeys("add");
    setItemsForConversation("add", []);
    setManualSourcesMap((prev) => {
      if (!prev.has("add")) return prev;
      const next = new Map(prev);
      next.delete("add");
      return next;
    });
    setConversationSourceSelections((prev) => clearConversationSourceSelection(prev, "add"));
    setDocsCollapsed(true);
    setHasSelectedSources(false);
  }, [setItemsForConversation]);

  // ── 反馈 hook（点踩确认、点赞、翻译） ──
  const {
    dislikeConfirmVisible,
    setDislikeConfirmVisible,
    dislikeConfirmLoading,
    setDislikeConfirmLoading,
    dislikeConfirmTarget,
    setDislikeConfirmTarget,
    closeDislikeConfirm,
    confirmDislike,
    handleFeedbackChange,
    handleTranslate,
  } = useFeedback({ setItems });

  const mapConversationItems = useCallback(
    (list: any[]): GetProp<ConversationsProps, "items"> =>
      list.map((item: any) => ({
        key: String(item.ID ?? item.id ?? ""),
        label: item.ConversationTitle ?? item.Conversation_ConversationTitle ?? "Untitled",
        group: NAV_GROUPS.askOpsMate,
        pinned:
          Number(
            item?.Conversation_Sort ??
              item?.conversation_sort ??
              item?.ConversationSort ??
              item?.conversationSort ??
              0,
          ) !== 0,
        createTime: item.Conversation_CreateTime ?? item.CreateTime ?? "",
      })),
    [],
  );

  const fetchConversationsPage = useCallback(
    async (page: number, append = false) => {
      if (historyConversationsLoadingRef.current) return;
      historyConversationsLoadingRef.current = true;
      if (append) setHistoryConversationsLoadingMore(true);

      try {
        const userId = user?.id;
        if (!userId) {
          setHistoryOpsConversations([]);
          setHistoryConversationsHasMore(false);
          historyConversationsHasMoreRef.current = false;
          return;
        }

        const res: any = await searchConversation({
          UserId: userId,
          Page: page,
          Limit: CONVERSATION_PAGE_SIZE,
          Sort: "Conversation_IsPinned desc, Conversation_LastActiveTime desc",
        });

        const list: any[] = Array.isArray(res?.Data) ? res.Data : [];
        const total = Number(res?.Count) || 0;
        const mapped = mapConversationItems(list);

        // 是否还有下一页：
        // 1) 本页返回 0 条 → 已经到底
        // 2) 本页返回不足一整页 → 已经到底
        // 3) 后端返回了 total，且 page * limit >= total → 已经到底
        // 4) 否则认为还有更多
        const hasMore =
          mapped.length === CONVERSATION_PAGE_SIZE &&
          (total <= 0 || page * CONVERSATION_PAGE_SIZE < total);

        if (append) {
          if (mapped.length > 0) {
            setHistoryOpsConversations((prev) => {
              const existedKeys = new Set(prev.map((item) => String(item?.key ?? "")));
              return [
                ...prev,
                ...mapped.filter((item) => !existedKeys.has(String(item?.key ?? ""))),
              ];
            });
          }
        } else {
          setHistoryOpsConversations(mapped);

          // 如果有 pending 新建的会话，自动选中
          if (pendingConversationId) {
            const found = mapped.find((m) => m.key === pendingConversationId);
            if (found) {
              setSelectedKeys(pendingConversationId);
            }
          }
        }

        setHistoryConversationsHasMore(hasMore);
        historyConversationsHasMoreRef.current = hasMore;
        historyConversationsPageRef.current = page;
      } catch (e) {
        console.error("fetchConversationsPage failed", e);
        if (!append) setHistoryOpsConversations([]);
        setHistoryConversationsHasMore(false);
        historyConversationsHasMoreRef.current = false;
      } finally {
        historyConversationsLoadingRef.current = false;
        setHistoryConversationsLoadingMore(false);
      }
    },
    [mapConversationItems, pendingConversationId, user?.id],
  );

  // ── 刷新会话列表 ──
  const refreshConversations = useCallback(async () => {
    await fetchConversationsPage(1, false);
  }, [fetchConversationsPage]);

  // 会话列表首次加载：由 Provider 负责（而非 ConversationsMenus），
  // 保证跨 /opsmate、/general-source、/personal-source、/study-source 路由切换时只加载一次、不被重复刷新。
  useEffect(() => {
    refreshConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMoreConversations = useCallback(async () => {
    if (!historyConversationsHasMoreRef.current || historyConversationsLoadingRef.current) return;
    await fetchConversationsPage(historyConversationsPageRef.current + 1, true);
  }, [fetchConversationsPage]);

  // ── 固定菜单项 ──
  const askOpsConversations = useMemo<GetProp<ConversationsProps, "items">>(
    () => [
      {
        key: "add",
        label: "New Ask",
        icon: (
          <img
            src={selectedKeys === "add" ? NewAskNoIcon : NewAskHuiIcon}
            alt=""
            aria-hidden
            style={{ width: 20, height: 20 }}
          />
        ),
        group: NAV_GROUPS.askOpsMate,
      },
      {
        key: "search",
        label: "Search Ask",
        icon: <img src={SearchAskHuiIcon} alt="" aria-hidden style={{ width: 20, height: 20 }} />,
        group: NAV_GROUPS.askOpsMate,
      },
      {
        key: "general-source",
        label: "General Source",
        icon: <i className="ri-file-search-line" style={{ fontSize: 20 }}></i>,
        group: NAV_GROUPS.askOpsMate,
      },
      {
        key: "personal-source",
        label: "Personal Source",
        icon: <i className="ri-folder-user-line" style={{ fontSize: 20 }}></i>,
        group: NAV_GROUPS.askOpsMate,
      },
      ...(canAccessStudySource
        ? [
            {
              key: "study-source",
              label: "Study Source",
              icon: <i className="ri-database-2-line" style={{ fontSize: 20 }}></i>,
              group: NAV_GROUPS.askOpsMate,
            },
          ]
        : []),
      // ...(canAccessAdminArea
      //   ? [
      //       {
      //         key: "admin",
      //         label: "Admin",
      //         icon: <DashboardOutlined style={{ fontSize: 20 }} />,
      //         group: NAV_GROUPS.adminConsole,
      //       },
      //     ]
      //   : []),
    ],
    [selectedKeys, canAccessAdminArea, canAccessStudySource],
  );

  const askOpsMateMenus = useMemo<MenuItem[]>(
    () => [
      {
        key: "add",
        icon: <img src={NewAskHuiIcon} alt="" aria-hidden style={{ width: 20, height: 20 }} />,
        label: "New Ask",
      },
      {
        key: "search",
        icon: <img src={SearchAskHuiIcon} alt="" aria-hidden style={{ width: 20, height: 20 }} />,
        label: "Search Ask",
      },
      ...(canAccessAdminArea
        ? [
            {
              key: "admin",
              icon: <DashboardOutlined style={{ fontSize: 20 }} />,
              label: "Admin",
            },
          ]
        : []),
      // { key: 'divider-1', type: 'divider' },

      // {
      //   key: 'my-mvr',
      //   icon: <img src={MyMVRHuiIcon} alt="" aria-hidden style={{ width: 20, height: 20 }} />,
      //   label: 'My MVR',
      // },
      // {
      //   key: 'my-studies',
      //   icon: <img src={MyStudiesHuiIcon} alt="" aria-hidden style={{ width: 20, height: 20 }} />,
      //   label: 'My Studies',
      // },
    ],
    [canAccessAdminArea],
  );

  const fixFooterOpsConversations = useMemo<GetProp<ConversationsProps, "items">>(
    () => [
      // {
      //   key: 'admin',
      //   label: 'Admin',
      //   icon: <DashboardOutlined style={{ fontSize: 20 }} />,
      // },
      // {
      //   key: 'my-mvr',
      //   label: 'My MVR',
      //   icon: <img src={MyMVRHuiIcon} alt="" aria-hidden style={{ width: 20, height: 20 }} />,
      //   group: 'MVR with OpsMate',
      // },
      // {
      //   key: 'my-studies',
      //   label: 'My Studies',
      //   icon: <img src={MyStudiesHuiIcon} alt="" aria-hidden style={{ width: 20, height: 20 }} />,
      //   group: 'MVR with OpsMate',
      // },
    ],
    [],
  );

  // ── Sources 数量 ──
  // 由右侧边栏 DocumentList 推送的“当前选中文档行数”。所有 Sources 徽标（发送按钮左侧、
  // 侧栏收起时的浮动按钮、侧栏标题）统一展示该值，保证与 Select All 下方选中行数一致。
  const [selectedSourcesCount, setSelectedSourcesCount] = useState(0);
  const [availableSourcesCount, setAvailableSourcesCount] = useState(0);

  // ── 历史会话加载 hook ──
  useSessionHistory({
    selectedKeys,
    pendingConversationId,
    setItems,
    setDocsCollapsed,
    setManualSources,
    getItemsForConversation,
  });

  // ── 重新生成 hook ──
  const { regenerateMessage } = useRegenerate({
    itemsRef,
    selectedKeys,
    onlyUseSelectedSources,
    selectedSourceGuidNames,
    docCategoryIds: questionLibraryScope.docCategoryIds,
    studySourceIds: questionLibraryScope.studySourceIds,
    includePersonalDocs: questionLibraryScope.includePersonalDocs,
    setItems,
  });

  // ── 消息气泡渲染 hook ──
  const { roles } = useMessageRoles({
    itemsRef,
    setDocsCollapsed,
    setHighlightedDocGuid,
    setAIReasoningVisible,
    setAIReasoning,
    handleTranslate,
    handleFeedbackChange,
    regenerateMessage,
    setDislikeConfirmTarget,
    setDislikeConfirmVisible,
  });

  // ── Context value ──
  const value = useMemo<OpsMateContextType>(
    () => ({
      collapsed,
      setCollapsed,
      selectedKeys,
      setSelectedKeys,
      resetConversation,
      askOpsConversations,
      askOpsMateMenus,
      openSearch,
      setOpenSearch,
      pageHeight,
      historyOpsConversations,
      fixFooterOpsConversations,
      docsCollapsed,
      setDocsCollapsed,
      hasSelectedSources,
      setHasSelectedSources,
      onlyUseSelectedSources,
      setOnlyUseSelectedSources,
      selectedSourceIndexes,
      setSelectedSourceIndexes,
      items,
      setItems,
      setItemsForConversation,
      manualSources,
      setManualSources,
      selectedSourceGuidNames,
      setSelectedSourceGuidNames,
      pendingConversationId,
      setPendingConversationId,
      migrateNewConversationSources,
      refreshConversations,
      loadMoreConversations,
      historyConversationsHasMore,
      historyConversationsLoadingMore,
      roles,
      addSourcesVisible,
      setAddSourcesVisible,
      questionLibraryScope,
      setQuestionLibraryScope,
      selectedSourcesCount,
      setSelectedSourcesCount,
      availableSourcesCount,
      setAvailableSourcesCount,
      listRef,
      aiReasoning,
      setAIReasoning,
      aiReasoningVisible,
      setAIReasoningVisible,
      dislikeConfirmVisible,
      setDislikeConfirmVisible,
      dislikeConfirmLoading,
      setDislikeConfirmLoading,
      dislikeConfirmTarget,
      setDislikeConfirmTarget,
      closeDislikeConfirm,
      confirmDislike,
      reasoningCollapsedByKey,
      setReasoningCollapsedByKey,
      regenerateMessage,
      highlightedDocGuid,
      setHighlightedDocGuid,
    }),
    [
      collapsed,
      selectedKeys,
      openSearch,
      docsCollapsed,
      hasSelectedSources,
      onlyUseSelectedSources,
      selectedSourceIndexes,
      items,
      manualSources,
      selectedSourceGuidNames,
      pendingConversationId,
      migrateNewConversationSources,
      historyOpsConversations,
      historyConversationsHasMore,
      historyConversationsLoadingMore,
      addSourcesVisible,
      questionLibraryScope,
      selectedSourcesCount,
      availableSourcesCount,
      aiReasoning,
      aiReasoningVisible,
      dislikeConfirmVisible,
      dislikeConfirmLoading,
      dislikeConfirmTarget,
      reasoningCollapsedByKey,
      resetConversation,
      refreshConversations,
      loadMoreConversations,
      roles,
      closeDislikeConfirm,
      confirmDislike,
      askOpsConversations,
      askOpsMateMenus,
      fixFooterOpsConversations,
      pageHeight,
      regenerateMessage,
      highlightedDocGuid,
      setDislikeConfirmLoading,
      setDislikeConfirmTarget,
      setDislikeConfirmVisible,
      setItems,
      setItemsForConversation,
      setManualSources,
      setOnlyUseSelectedSources,
      setSelectedSourceIndexes,
    ],
  );

  return <OpsMateContext.Provider value={value}>{children}</OpsMateContext.Provider>;
}
